import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy,
  Injector,
  inject,
  signal,
  computed,
} from '@angular/core'
import { form } from '@angular/forms/signals'
import { PageEvent } from '@angular/material/paginator'
import { Router } from '@angular/router'
import { KirjanpidonProjekti } from '@lemon/core-firebase/model/kirjanpito-projektit'
import { TypeaheadToimittaja, TypeaheadToimittajaDokumentti } from '@lemon/core-firebase/model/lasku'
import { TositeHakutulos, TositeLaji, TositteetHakuPyyntoLemonator, TositteetHakuVastaus } from '@lemon/core-firebase/model/tosite'
import { FirestoreIndeksoija } from '@lemon/core-firebase/service/firestore.indeksoija'
import { LaskuUriService } from '@lemon/core-firebase/service/lasku/lasku-uri.service'
import { NumberDate, TuettuKieli } from '@lemon/core/model/common'
import { DateService } from '@lemon/core/service/date.service'
import { FirebaseLemonaid } from 'app/_angular/service/firebase-lemonator.service'
import { KirjanpitoService } from 'app/_angular/service/kirjanpito/kirjanpito.service'
import { TositeHakuAikavali, TositeHakuStateService } from 'app/_angular/service/tosite/tosite-haku-state.service'
import { EMPTY, Subject, from, of } from 'rxjs'
import { debounceTime, map, switchMap, takeUntil } from 'rxjs/operators'
import { AsiakasService } from '../../../_angular/service/asiakas/asiakas.service'
import { arvonMuutokset, asetaArvo } from '../../../_jaettu-angular/_lomakkeet/arvon-muutokset'
import { LemonTranslationService } from '../../../_jaettu-angular/service/lemon-translation.service'
import { Asiakas, AsiakkaanMaksutapa } from '../../../_jaettu-lemonator/model/asiakas'
import { AsiakkaanMaksutavat } from '../../../_jaettu-lemonator/service/asiakas-jaettu.service'

interface MaksutapaChip {
  tunniste: number
  nimi: string
}

interface ProjektiChip {
  avain: string
  nimi: string
}

interface ToimittajaChip {
  avain: string
  nimi: string
}

export interface TositeHakuRivi {
  avain: string
  paivamaara: NumberDate | null
  toimittajanNimi: string | null
  lisatiedot: string
  maksutapaNimi: string | null
  summa: number | null
  projektiAvain: string | null
  onOstolasku: boolean
}

interface TositeHakuLomake {
  hakuteksti: string
  alkuPvm: Date | null
  loppuPvm: Date | null
}

type TositeHakuPaneeli = 'aikavali' | 'maksutapa' | 'toimittaja' | 'projekti' | 'tositelaji'

@Component({
  selector: 'app-tosite-haku',
  templateUrl: './tosite-haku.component.html',
  styleUrls: ['./tosite-haku.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class TositeSelailuHakuComponent implements OnInit, OnChanges, OnDestroy {
  @Input() avoinna = false
  @Input() oletusTositelaji: TositeLaji = 'tositteet'
  @Output() suljettu = new EventEmitter<void>()

  // Kenttä on @if-lohkon sisällä, joten setter laukeaa vasta kun dialogi avataan ja kenttä ilmestyy DOMiin
  @ViewChild('hakukentta', { read: ElementRef }) set hakukentta(kentta: ElementRef<HTMLInputElement> | undefined) {
    if (kentta) {
      setTimeout(() => kentta.nativeElement.focus())
    }
  }

  lomake = form(signal<TositeHakuLomake>({ hakuteksti: '', alkuPvm: null, loppuPvm: null }))
  aikavali: TositeHakuAikavali = 'viimeiset12kk'
  readonly aikavaliPresetit: TositeHakuAikavali[] = ['viimeiset12kk', 'viimeiset3kk', 'viimeiset30pv', 'tamaVuosi', 'kaikki']
  readonly aikavalinTeksti = signal('')
  readonly avoinPaneeli = signal<TositeHakuPaneeli | null>(null)

  readonly kaikkiMaksutavat = signal<AsiakkaanMaksutapa[]>([])
  valitutMaksutavat = new Set<number>()

  readonly kaikkiProjektit = signal<KirjanpidonProjekti[]>([])
  readonly projektitMap = computed(() => new Map(this.kaikkiProjektit().map(p => [p.avain, p])))
  valitutProjektit = new Set<string>()
  readonly naytaProjektitSuodatin = computed(() => this.kaikkiProjektit().length > 0)

  readonly kaikkiToimittajat = signal<TypeaheadToimittaja[]>([])
  valitutToimittajat = new Set<string>()

  tositelaji: TositeLaji = 'tositteet'

  readonly tulokset = signal<TositeHakuRivi[]>([])
  readonly haetaan = signal(false)
  readonly kokonaismaara = signal(0)
  sivu = 0
  sivunKoko = 50
  readonly sivukoot = [50, 100, 200]

  private _asiakas: Asiakas | null = null
  private readonly _maksutavatMap = signal(new Map<number, AsiakkaanMaksutapa>())
  private readonly _toimittajaNimet = computed(() => new Map(this.kaikkiToimittajat().map(t => [t.avain, t.nimi])))
  private _hakuTrigger = new Subject<void>()
  private _edellinenHakuJson: string | null = null
  private _ngUnsubscribe = new Subject<void>()
  private readonly _injector = inject(Injector)
  private _nykyinenKieli: TuettuKieli | null = null

  constructor(
    private _firebaseLemonaid: FirebaseLemonaid,
    private _dateService: DateService,
    private _asiakasService: AsiakasService,
    private _kirjanpitoService: KirjanpitoService,
    private _laskuUriService: LaskuUriService,
    private _router: Router,
    private _translationService: LemonTranslationService,
    private _indeksoija: FirestoreIndeksoija,
    private _hakuStateService: TositeHakuStateService,
  ) {}

  ngOnInit() {
    this._translationService.currentLanguageObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(kieli => {
      this._nykyinenKieli = kieli
      this._paivitaAikavalinTeksti()
    })

    this._hakuTrigger
      .pipe(
        debounceTime(500),
        switchMap(() => {
          if (!this.onkoHakuehtoja || !this._asiakas) {
            return of({ vastaus: null as TositteetHakuVastaus | null, pyyntoJson: null as string | null })
          }
          const pyynto = this._rakennaHakupyynto(this._asiakas)
          const pyyntoJson = JSON.stringify(pyynto)
          // Ei lähetetä uutta pyyntöä, jos hakuehdot ovat samat kuin viimeksi sovelletussa vastauksessa
          if (pyyntoJson === this._edellinenHakuJson) {
            this.haetaan.set(false)
            return EMPTY
          }
          return from(this._suoritaHaku(pyynto)).pipe(map(vastaus => ({ vastaus, pyyntoJson })))
        }),
        takeUntil(this._ngUnsubscribe),
      )
      .subscribe(({ vastaus, pyyntoJson }) => this._kasitteleHakuvastaus(vastaus, pyyntoJson))

    arvonMuutokset(this.lomake.hakuteksti, { injector: this._injector })
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(() => this.hakutekstiMuuttui())

    const tallennettuHakutila = this._hakuStateService.onkoHakutilaTallennettu(this._router.url) ? this._hakuStateService.palautaHakutila() : null
    if (tallennettuHakutila) {
      asetaArvo(this.lomake.hakuteksti, tallennettuHakutila.hakuteksti ?? '', { ilmoita: false })
      this.aikavali = tallennettuHakutila.aikavali
      asetaArvo(this.lomake.alkuPvm, tallennettuHakutila.alkuPvm ? this._dateService.numberToDate(tallennettuHakutila.alkuPvm) : null, { ilmoita: false })
      asetaArvo(this.lomake.loppuPvm, tallennettuHakutila.loppuPvm ? this._dateService.numberToDate(tallennettuHakutila.loppuPvm) : null, { ilmoita: false })
      this.valitutMaksutavat = new Set(tallennettuHakutila.valitutMaksutavat)
      this.valitutToimittajat = new Set(tallennettuHakutila.valitutToimittajat)
      this.valitutProjektit = new Set(tallennettuHakutila.valitutProjektit)
      this.tositelaji = tallennettuHakutila.tositelaji
      this.sivu = tallennettuHakutila.sivu
      this.sivunKoko = tallennettuHakutila.sivunKoko
      this._paivitaAikavalinTeksti()
      this._hakuTrigger.next()
    } else {
      this.tositelaji = this.oletusTositelaji
    }

    this._asiakasService.nykyinenAsiakasObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(asiakas => {
      const asiakasVaihtui = !!this._asiakas && !!asiakas && this._asiakas.avain !== asiakas.avain
      if (asiakasVaihtui) {
        this._tyhjennaAsiakaskohtaisetValinnat()
      }
      this._asiakas = asiakas
      if (asiakas && this.avoinna) {
        this._hakuTrigger.next()
      }
    })

    this._asiakasService.nykyisenAsiakkaanKaikkiMaksutavatObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(maksutavat => {
      this._maksutavatMap.set(new Map((maksutavat ?? []).map(mt => [mt.tunniste, mt])))
    })

    this._asiakasService.nykyisenAsiakkaanMaksutavatObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(maksutavat => {
      this.kaikkiMaksutavat.set([AsiakkaanMaksutavat.MAKSUTAPA_SAHKOISET_LASKUT, ...(maksutavat ?? [])])
    })

    this._kirjanpitoService.kirjanpidonProjektitObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(projektit => {
      this.kaikkiProjektit.set(projektit ?? [])
      if (!this.naytaProjektitSuodatin() && this.valitutProjektit.size > 0) {
        this.valitutProjektit.clear()
        if (this.avoinPaneeli() === 'projekti') {
          this.avoinPaneeli.set(null)
        }
        this._haeAlusta()
      }
    })

    this._asiakasService.nykyinenAsiakasAvainObservable
      .pipe(
        switchMap(avainTiedot => {
          if (!avainTiedot?.avain) {
            return of<TypeaheadToimittaja[]>([])
          }
          const typeaheadUri = this._laskuUriService.getTypeaheadToimittajaDokumentinUriNew(avainTiedot.avain)
          return this._firebaseLemonaid
            .firestoreDoc<TypeaheadToimittajaDokumentti>(typeaheadUri)
            .listen()
            .pipe(
              map(dokumentti =>
                Object.entries(dokumentti ?? {}).map(([avain, arvo]) => ({ avain, numero: arvo.numero, nimi: arvo.nimi, ytunnus: arvo.ytunnus, poistettu: arvo.poistettu }) as TypeaheadToimittaja),
              ),
            )
        }),
        takeUntil(this._ngUnsubscribe),
      )
      .subscribe(toimittajat => {
        this.kaikkiToimittajat.set((toimittajat ?? []).filter(t => !t.poistettu).sort((a, b) => (a.nimi ?? '').localeCompare(b.nimi ?? '', 'fi')))
      })
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!changes.avoinna) {
      return
    }
    if (changes.avoinna.currentValue) {
      this._hakuTrigger.next()
    } else {
      this.tulokset.set([])
      this.kokonaismaara.set(0)
      // Nollaus varmistaa, että uudelleen avattaessa haetaan tuoreet tulokset samoillakin ehdoilla
      this._edellinenHakuJson = null
      this._suljePaneelit()
    }
  }

  @HostListener('window:keydown.escape')
  suljeEscapella() {
    if (!this.avoinna) {
      return
    }
    if (this._onkoJokinPaneeliAuki()) {
      this._suljePaneelit()
    } else {
      this.sulje()
    }
  }

  @HostListener('document:click', ['$event'])
  dokumenttiaKlikattu(event: MouseEvent) {
    if (!this.avoinna || !this._onkoJokinPaneeliAuki()) {
      return
    }
    const klikattiinPaneelia = event.composedPath().some(solmu => solmu instanceof Element && (solmu.classList.contains('dropdown-wrap') || solmu.classList.contains('cdk-overlay-container')))
    if (klikattiinPaneelia) {
      return
    }
    this._suljePaneelit()
  }

  private _tyhjennaAsiakaskohtaisetValinnat() {
    this.valitutMaksutavat.clear()
    this.valitutToimittajat.clear()
    this.valitutProjektit.clear()
    this.tulokset.set([])
    this.kokonaismaara.set(0)
    this.sivu = 0
    this._edellinenHakuJson = null
    this._suljePaneelit()
  }

  private _onkoJokinPaneeliAuki(): boolean {
    return this.avoinPaneeli() !== null
  }

  private _suljePaneelit() {
    this.avoinPaneeli.set(null)
  }

  sulje() {
    this.suljettu.emit()
  }

  taustaaKlikattu(event: MouseEvent) {
    if (event.target === event.currentTarget) {
      this.sulje()
    }
  }

  hakutekstiMuuttui() {
    this._haeAlusta()
  }

  sivuMuuttui(event: PageEvent) {
    this.sivu = event.pageIndex
    this.sivunKoko = event.pageSize
    this._hakuTrigger.next()
  }

  private _haeAlusta() {
    this.sivu = 0
    this._hakuTrigger.next()
  }

  vaihdaPaneeli(paneeli: TositeHakuPaneeli) {
    this.avoinPaneeli.set(this.avoinPaneeli() === paneeli ? null : paneeli)
  }

  valitseAikavaliPreset(preset: TositeHakuAikavali) {
    this.aikavali = preset
    asetaArvo(this.lomake.alkuPvm, null, { ilmoita: false })
    asetaArvo(this.lomake.loppuPvm, null, { ilmoita: false })
    this.aikavalinTeksti.set('')
    this.avoinPaneeli.set(null)
    this._haeAlusta()
  }

  valitseAikavali() {
    if (!this.lomake.alkuPvm().value() && !this.lomake.loppuPvm().value()) {
      return
    }
    this.aikavali = 'mukautettu'
    this._paivitaAikavalinTeksti()
    this.avoinPaneeli.set(null)
    this._haeAlusta()
  }

  poistaAikavali() {
    this.valitseAikavaliPreset('kaikki')
  }

  onkoMaksutapaValittu(tunniste: number): boolean {
    return this.valitutMaksutavat.has(tunniste)
  }

  vaihdaMaksutapa(tunniste: number) {
    if (this.valitutMaksutavat.has(tunniste)) {
      this.valitutMaksutavat.delete(tunniste)
    } else {
      this.valitutMaksutavat.add(tunniste)
    }
    this._haeAlusta()
  }

  poistaMaksutapaValinta(tunniste: number) {
    this.valitutMaksutavat.delete(tunniste)
    this._haeAlusta()
  }

  tyhjennaMaksutavat() {
    if (this.valitutMaksutavat.size === 0) {
      return
    }
    this.valitutMaksutavat.clear()
    this._haeAlusta()
  }

  onkoToimittajaValittu(avain: string): boolean {
    return this.valitutToimittajat.has(avain)
  }

  vaihdaToimittaja(avain: string) {
    if (this.valitutToimittajat.has(avain)) {
      this.valitutToimittajat.delete(avain)
    } else {
      this.valitutToimittajat.add(avain)
    }
    this._haeAlusta()
  }

  poistaToimittajaValinta(avain: string) {
    this.valitutToimittajat.delete(avain)
    this._haeAlusta()
  }

  tyhjennaToimittajat() {
    if (this.valitutToimittajat.size === 0) {
      return
    }
    this.valitutToimittajat.clear()
    this._haeAlusta()
  }

  onkoProjektiValittu(avain: string): boolean {
    return this.valitutProjektit.has(avain)
  }

  vaihdaProjekti(avain: string) {
    if (this.valitutProjektit.has(avain)) {
      this.valitutProjektit.delete(avain)
    } else {
      this.valitutProjektit.add(avain)
    }
    this._haeAlusta()
  }

  poistaProjektiValinta(avain: string) {
    this.valitutProjektit.delete(avain)
    this._haeAlusta()
  }

  tyhjennaProjektit() {
    if (this.valitutProjektit.size === 0) {
      return
    }
    this.valitutProjektit.clear()
    this._haeAlusta()
  }

  vaihdaTositelaji(arvo: TositeLaji) {
    if (this.tositelaji === arvo) {
      this.avoinPaneeli.set(null)
      return
    }
    this.tositelaji = arvo
    this.avoinPaneeli.set(null)
    this._haeAlusta()
  }

  get valitutMaksutavatLista(): MaksutapaChip[] {
    return Array.from(this.valitutMaksutavat).map(tunniste => ({
      tunniste,
      nimi: this._maksutavatMap().get(tunniste)?.nimi ?? '',
    }))
  }

  get valitutProjektitLista(): ProjektiChip[] {
    return Array.from(this.valitutProjektit).map(avain => ({
      avain,
      nimi: this.projektitMap().get(avain)?.nimi ?? '',
    }))
  }

  get valitutToimittajatLista(): ToimittajaChip[] {
    return Array.from(this.valitutToimittajat).map(avain => ({
      avain,
      nimi: this._toimittajaNimet().get(avain) ?? '',
    }))
  }

  get onkoHakuehtoja(): boolean {
    return this.lomake.hakuteksti().value().trim().length > 0 || this.aikavali !== 'kaikki' || this.valitutMaksutavat.size > 0 || this.valitutProjektit.size > 0 || this.valitutToimittajat.size > 0
  }

  avaaTosite(tositteenAvain: string) {
    if (!this._asiakas) {
      return
    }
    this._hakuStateService.tallennaHakutila({
      hakuteksti: this.lomake.hakuteksti().value(),
      aikavali: this.aikavali,
      alkuPvm: this.lomake.alkuPvm().value() ? this._dateService.dateToNumber(this.lomake.alkuPvm().value()) : null,
      loppuPvm: this.lomake.loppuPvm().value() ? this._dateService.dateToNumber(this.lomake.loppuPvm().value()) : null,
      valitutMaksutavat: Array.from(this.valitutMaksutavat),
      valitutToimittajat: Array.from(this.valitutToimittajat),
      valitutProjektit: Array.from(this.valitutProjektit),
      tositelaji: this.tositelaji,
      sivu: this.sivu,
      sivunKoko: this.sivunKoko,
      paluureitti: this._router.url,
    })
    const onOstolasku = this.tulokset().find(rivi => rivi.avain === tositteenAvain)?.onOstolasku
    if (onOstolasku) {
      this._router.navigate(['asiakkaat', this._asiakas.avain, 'ostolaskut', 'selaa', tositteenAvain])
    } else {
      this._router.navigate(['asiakkaat', this._asiakas.avain, 'tositteet', 'selaa', tositteenAvain])
    }
  }

  private async _suoritaHaku(pyynto: TositteetHakuPyyntoLemonator): Promise<TositteetHakuVastaus | null> {
    this.haetaan.set(true)
    try {
      return await this._firebaseLemonaid.functionsCall<TositteetHakuPyyntoLemonator, TositteetHakuVastaus>('tositteet-etsiTositeLemonSearchLemonator', pyynto, undefined, 'europe-north1')
    } catch {
      return null
    }
  }

  private _kasitteleHakuvastaus(vastaus: TositteetHakuVastaus | null, pyyntoJson: string | null) {
    this.haetaan.set(false)
    if (!vastaus || vastaus.e) {
      this.tulokset.set([])
      this.kokonaismaara.set(0)
      this._edellinenHakuJson = null
      return
    }
    this._edellinenHakuJson = pyyntoJson
    this.tulokset.set((vastaus.tositteet ?? []).map(t => this._luoListarivi(t)))
    this.kokonaismaara.set(vastaus.kokonaismaara ?? 0)
  }

  private _rakennaHakupyynto(asiakas: Asiakas): TositteetHakuPyyntoLemonator {
    const pyynto: TositteetHakuPyyntoLemonator = {
      asiakasAvain: asiakas.avain,
    }

    const teksti = this.lomake.hakuteksti().value().trim()
    if (teksti) {
      pyynto.vapaahaku = teksti

      const toimittajaAvaimet = this._annaTekstiaVastaavatToimittajaAvaimet(teksti)
      if (toimittajaAvaimet.length > 0) {
        pyynto.vapaahakuToimittajaAvaimet = toimittajaAvaimet
      }

      const projektiAvaimet = this._annaTekstiaVastaavatProjektiAvaimet(teksti)
      if (projektiAvaimet.length > 0) {
        pyynto.vapaahakuProjektiAvaimet = projektiAvaimet
      }
    }

    const today = this._dateService.currentNumberDate()
    switch (this.aikavali) {
      case 'viimeiset12kk':
        pyynto.alkuPvm = this._dateService.lisaaKuukausiaNumero(today, -12)
        pyynto.loppuPvm = today
        break
      case 'viimeiset3kk':
        pyynto.alkuPvm = this._dateService.lisaaKuukausiaNumero(today, -3)
        pyynto.loppuPvm = today
        break
      case 'viimeiset30pv':
        pyynto.alkuPvm = this._dateService.lisaaPaiviaNumber(today, -30)
        pyynto.loppuPvm = today
        break
      case 'tamaVuosi':
        pyynto.alkuPvm = this._dateService.dateToNumber(new Date(new Date().getFullYear(), 0, 1))
        pyynto.loppuPvm = today
        break
      case 'mukautettu':
        if (this.lomake.alkuPvm().value()) {
          pyynto.alkuPvm = this._dateService.dateToNumber(this.lomake.alkuPvm().value())
        }
        if (this.lomake.loppuPvm().value()) {
          pyynto.loppuPvm = this._dateService.dateToNumber(this.lomake.loppuPvm().value())
        }
        break
      case 'kaikki':
        break
    }

    if (this.valitutMaksutavat.size > 0) {
      pyynto.maksutavat = Array.from(this.valitutMaksutavat)
    }

    if (this.valitutProjektit.size > 0) {
      pyynto.projektiAvaimet = Array.from(this.valitutProjektit)
    }

    if (this.valitutToimittajat.size > 0) {
      pyynto.toimittajaAvaimet = Array.from(this.valitutToimittajat)
    }

    pyynto.tositelaji = this.tositelaji
    pyynto.ylaraja = this.sivunKoko
    pyynto.sivu = this.sivu

    return pyynto
  }

  private _annaTekstiaVastaavatToimittajaAvaimet(teksti: string): string[] {
    const hakusanat = this._annaHakusanat(teksti)
    return this.kaikkiToimittajat()
      .filter(toimittaja => this._nimiSisaltaaJonkinHakusanan(toimittaja.nimi, hakusanat))
      .map(toimittaja => toimittaja.avain)
  }

  private _annaTekstiaVastaavatProjektiAvaimet(teksti: string): string[] {
    const hakusanat = this._annaHakusanat(teksti)
    return this.kaikkiProjektit()
      .filter(projekti => this._nimiSisaltaaJonkinHakusanan(projekti.nimi, hakusanat))
      .map(projekti => projekti.avain)
  }

  private _annaHakusanat(teksti: string): string[] {
    return this._indeksoija
      .poistaValimerkitJaNormalisoiValilyonnit(teksti)
      .split(' ')
      .filter(sana => sana.length > 0)
  }

  private _nimiSisaltaaJonkinHakusanan(nimi: string | null | undefined, hakusanat: string[]): boolean {
    const normalisoituNimi = this._indeksoija.poistaValimerkitJaNormalisoiValilyonnit(nimi ?? '')
    return hakusanat.some(sana => normalisoituNimi.includes(sana))
  }

  private _luoListarivi(tosite: TositeHakutulos): TositeHakuRivi {
    return {
      avain: tosite.a,
      paivamaara: tosite.p ?? null,
      toimittajanNimi: (tosite.t ? this._toimittajaNimet().get(tosite.t) : null) ?? tosite.n ?? null,
      summa: tosite.s ?? null,
      maksutapaNimi: this._maksutavatMap().get(tosite.m)?.nimi ?? null,
      lisatiedot: tosite.e ?? '',
      projektiAvain: tosite.j ?? null,
      onOstolasku: tosite.l === 'o' || tosite.l === 'm',
    }
  }

  private _paivitaAikavalinTeksti() {
    const alkuPvm = this.lomake.alkuPvm().value()
    const loppuPvm = this.lomake.loppuPvm().value()
    if (!alkuPvm && !loppuPvm) {
      this.aikavalinTeksti.set('')
      return
    }

    if (!this._nykyinenKieli) {
      return
    }

    if (alkuPvm && loppuPvm) {
      const alkuStr = this._dateService.muotoilePaivaDate(alkuPvm, this._nykyinenKieli)
      const loppuStr = this._dateService.muotoilePaivaDate(loppuPvm, this._nykyinenKieli)
      this.aikavalinTeksti.set(`${alkuStr} - ${loppuStr}`)
    } else if (alkuPvm) {
      const alkaenTeksti = this._translationService.lokalisoiKielella('tositteet.haku.alkaen', this._nykyinenKieli)
      const alkuStr = this._dateService.muotoilePaivaDate(alkuPvm, this._nykyinenKieli)
      this.aikavalinTeksti.set(`${alkaenTeksti} ${alkuStr}`)
    } else if (loppuPvm) {
      const paattyenTeksti = this._translationService.lokalisoiKielella('tositteet.haku.paattyen', this._nykyinenKieli)
      const loppuStr = this._dateService.muotoilePaivaDate(loppuPvm, this._nykyinenKieli)
      this.aikavalinTeksti.set(`${paattyenTeksti} ${loppuStr}`)
    }
  }
}
